import { formatDate } from "date-fns";
import React, { useEffect } from "react"

import { ASSETS } from "../../api/Registry"
import { BlockBuster } from "./cast/BlockBuster"
import HttpServices from "../../services/HttpServices"
import { RouteHydration } from "../../utils/Helpers"
import { Loading } from "../../components/utils/Loading"
import { RadioButton } from "../../components/utils/RadioButton"
import { FullScreenModal } from "../../lib/fragments/FullScreenModal"
import documentDraftLight from "../../resources/images/document_draft.png"
import documentPublishedLight from "../../resources/images/document_published.png"
import { G_onInputBlurHandler, G_onInputChangeHandler } from "../../components/utils/InputHandlers"
import { toast } from "react-toastify"

export const AssetFormPreview = ({ show, showOrHide, category, type }) => {
    const [state, setstate] = React.useState({
        input: [],
        faults: [],
        stride: 0,
        version: null,
        posting: false,
        httpStatus: 200,
        status: 'pending',
        activeVersions: [],
        data: {
            sections: [],
            preview: null,
            mappedAXT: [],
            currentSection: null,
        }
    })

    const previewChoices = [
        {
            key: "published",
            label: "Published Form",
            subtitle: "View the currently active published version of this form"
        },
        {
            key: "draft",
            label: "Drafted Version",
            subtitle: "View most recent saved draft, includes unpublished changes"
        }
    ]

    // Define the mapping for AXT attribute keys to their full names
    const axtMapping: Record<string, string> = {
        k: "label",
        b: "block",
        s: "subtitle",
        p: "properties",
        xid: "xid",
    };

    /**
     * Effect triggered when the `show`, `category`, or `type` props change.
     * 
     * This function fetches available preview versions for the given category and type.
     * - If a single active version exists, fetches the attributes for that version.
     * - If multiple active versions exist, sets the status to 'fulfilled'
     *   and show the available versions to the user
     * - Updates the component state based on the result of the API request.
     */
    useEffect(() => {
        if (show) {
            /**
             * Fetches available preview versions for the specified category and type.
             * - Sets the `status` to 'pending' during the request.
             * - Filters active versions and handles different scenarios based on the number of active versions.
             * - If successful, fetches attributes for the selected version or marks as 'fulfilled'.
             * 
             * @returns {Promise<void>} - A promise indicating the completion of the fetch operation.
             */
            const fetchAvailablePreviewVersions = async () => {
                setstate(prevState => ({
                    ...prevState,
                    status: 'pending'
                }));

                let { data, status, version, httpStatus, activeVersions } = state;

                try {
                    const previewVersRoute = [":category", ":type"]
                        .reduce((route, param, index) =>
                            RouteHydration(route, param, [category.uuid, type.uuid][index]),
                            ASSETS.FORM_VERSIONS
                        );

                    const response: any = await HttpServices.httpGet(previewVersRoute);
                    httpStatus = response.status;

                    if (httpStatus === 200) {
                        const payload: any = response.data.payload;

                        // Filter available versions and collect their keys
                        activeVersions = payload.available
                            .filter((item: any) => item.active)
                            .map((item: any) => item.key);

                        if (activeVersions.length === 1) {
                            // If exactly one active version exists, fetch its attributes
                            version = activeVersions[0];

                            fetchAttributesForVersion(version);
                        } else if (activeVersions.length > 1) {
                            status = 'fulfilled';
                        } else {
                            status = 'rejected';
                        }
                    } else {
                        status = 'rejected';
                    }
                } catch (error) {
                    httpStatus = 500;
                    status = 'rejected';
                }

                setstate(prevState => ({
                    ...prevState,
                    data,
                    status,
                    version,
                    httpStatus,
                    activeVersions,
                }));
            };

            fetchAvailablePreviewVersions();
        }
    }, [show, category, type]);

    /**
     * Helper function to decode and parse base64 encoded `properties` into a valid JSON object.
     * If the decoding or parsing fails, it logs an error and returns an empty object.
     * 
     * @param {string} encoded - The base64 encoded properties string.
     * @returns {object} - The parsed properties or an empty object in case of an error.
     */
    const parseProperties = (encoded: string | null): any => {
        if (!encoded) {
            return {}; // Return an empty object if properties is null or undefined
        }

        try {
            return JSON.parse(atob(encoded) || "{}");
        } catch {
            console.error("Failed to parse attribute properties");
            return {};
        }
    }

    /**
     * Fetches attributes for a specific version, processes them, and updates the state with the parsed data.
     * 
     * This function:
     * - Constructs the API route dynamically based on provided `category`, `type`, and `version`.
     * - Makes an HTTP GET request to fetch the attributes for the specified version.
     * - If the request is successful:
     *     - Processes the response to extract and structure the preview data, input values, faults, and sections.
     *     - Maps the AXT (Attributes) keys to their full names.
     *     - Sets the initial values for the form inputs and faults.
     * - In case of failure (HTTP status other than 200 or catch block), updates the status to 'rejected'.
     * 
     * @param {any} version - The version for which the attributes should be fetched.
     */
    const fetchAttributesForVersion = async (version: any) => {
        let { data, input, faults, stride, status } = state;

        try {
            let httpStatus = 200;
            let response: any = null;

            const previewVersRoute = [":category", ":type", ":version"].reduce(
                (route, param, index) => RouteHydration(route, param, [category.uuid, type.uuid, version][index]),
                ASSETS.PREVIEW_FORM
            );

            response = await HttpServices.httpGet(previewVersRoute);
            httpStatus = response.status;

            if (httpStatus === 200) {
                const payload: any = response.data.payload;
                data.preview = payload.preview;

                input = [];
                faults = [];
                data.sections = [];

                data.preview.forEach((element: any) => {
                    const sectionObject = {
                        oid: element.oid,
                        label: element.k,
                        subtitle: element.s,
                    };

                    const { oid, axt } = element;

                    /**
                     * Use `reduce` to process each attribute (axt) within the current section.
                     * For each attribute:
                     * - Parse properties and determine initial value based on its type (e.g., "glyph").
                     * - Initialize attribute input and error arrays for the section.
                     */
                    const { attrInputArr, attrErrorArr } = axt.reduce(
                        (acc: any, { xid, b, p }) => {
                            const parsedProps = parseProperties(p);
                            let initialValue: any

                            if (b === "arbiter" && parsedProps.display?.type === "Checkboxes") {
                                initialValue = []; // Checkbox input should be an array
                            } else if (b === "glyph" && parsedProps.restrictions?.name === "Numerical") {
                                initialValue = 0;
                            } else {
                                initialValue = ""
                            }

                            acc.attrInputArr.push({ [xid]: initialValue });
                            acc.attrErrorArr.push({ [xid]: "" });

                            return acc; // Return the accumulator to continue the reduction
                        },
                        { attrInputArr: [], attrErrorArr: [] }
                    );

                    input.push({ [oid]: attrInputArr });
                    faults.push({ [oid]: attrErrorArr });

                    data.sections.push(sectionObject);
                });

                data.mappedAXT = [];
                data.currentSection = data.sections[0];

                /**
                 * Extract the AXT data for the current section and map the short keys to full names.
                 * This uses the `axtMapping` to convert the keys and returns a mapped version of the AXT.
                 */
                const unmappedAXT = data.preview.find((item: any) => item.oid === data.currentSection.oid)?.axt || [];

                data.mappedAXT = unmappedAXT.map((axt: any) =>
                    Object.keys(axt).reduce((mapped: any, key) => {
                        mapped[axtMapping[key]] = axt[key]; // Map short key to full key name
                        return mapped;
                    }, {})
                );

                stride = data.sections.findIndex((item: any) => item.oid === data.currentSection.oid);
                status = "fulfilled";
            } else {
                // If the HTTP status is not 200, mark the status as "rejected"
                status = 'rejected';
            }
        } catch (error) {
            // If there’s an error in the try block, set the status to "rejected"
            status = 'rejected';
        }

        setstate((prevState: any) => ({
            ...prevState,
            posting: false,
            data,
            input,
            faults,
            status,
            stride,
        }));
    }

    /**
     * Modifies a specific field inside a section of the state (input/faults).
     *
     * @param {Array} sections - The state array (e.g., state.input or state.faults).
     * @param {string} oid - The unique section identifier (object key).
     * @param {string} name - The field name (key inside the section).
     * @param {any} newValue - The new value to be assigned to the field.
     * @returns {Array} - The modified array with the modified field value.
     */
    const modFacet = (sections: any[], oid: string, name: string, newValue: any, isCheckbox: boolean = false): any[] => {
        return sections.map((section) =>
            section[oid]
                ? {
                    [oid]: section[oid].map((field) => {
                        if (Object.keys(field)[0] === name) {
                            let existingValues = field[name] || [];

                            if (isCheckbox) {
                                // Handle checkbox values as an array
                                if (Array.isArray(existingValues)) {
                                    existingValues = existingValues.includes(newValue)
                                        ? existingValues.filter((v) => v !== newValue) // Remove if exists
                                        : [...existingValues, newValue]; // Add if not exists
                                } else {
                                    existingValues = [newValue];
                                }

                                return { [name]: existingValues };
                            } else {
                                // Regular input field (single value)
                                return { [name]: newValue };
                            }
                        }
                        return field;
                    })
                }
                : section
        );
    };

    /**
     * Handles input changes and modified the corresponding state values.
     *
     * This function ensures:
     * - Only the targeted field is modifiedd, leaving others unchanged.
     * - The function is efficient and maintains immutability.
     *
     * @param {Event} e - The input change event.
     * @param {string} oid - The unique section identifier in `state.input` and `state.faults`.
     */
    const onChangeHandler = (e: any, oid: string) => {
        const { posting, input } = state;

        // Prevent state modifications if a request is currently being posted
        if (posting) return;

        const { name, value } = e.target;
        const currentValue = selectFacet(input, oid, name);

        let output: any = G_onInputChangeHandler(e);

        // Convert value to number if the current input expects a number
        if (typeof currentValue === "number") {
            output.value = value === "" || isNaN(Number(value)) ? 0 : Number(value);
        }

        setstate((prev) => ({
            ...prev,
            input: modFacet(prev.input, oid, name, output.value),
            faults: modFacet(prev.faults, oid, name, output.error),
        }));
    };

    /**
     * Handles the blur event for form inputs.
     * 
     * - Updates the specific value in both `input` and `faults` states 
     *   based on the field that triggered the event.
     * - Applies changes only if `posting` is false to prevent state updates 
     *   during ongoing form submissions or operations.
     * - Utilizes `G_onInputBlurHandler` for validation and formatting.
     * - Uses `modFacet` to update the nested structure in `input` and `faults`.
     * - `k` represents the human-readable label of the field (e.g., `OS Name`)
     *   and is used to display validation errors in a user-friendly way 
     *   instead of showing the raw `xid` (e.g., `76f81d6a`).
     *
     * @param {Event} e - The blur event triggered by the input field.
     * @param {string} oid - The unique identifier for the section in `input` and `faults`.
     * @param {string} k - The label of the field, used for displaying errors post-validation.
     */
    const onInputBlur = (e: any, oid: string, k: string) => {
        const { posting, input } = state;

        // Prevent state modifications if a request is currently being posted
        if (posting) return;

        const { name, value } = e.target;
        const currentValue = selectFacet(input, oid, name)

        let output = G_onInputBlurHandler(e, k);
        output.error = output.error.replace(e.target.name, "")

        if (typeof currentValue === "number") {
            if (isNaN(value) || value === "") {
                output.error = "Value must be a valid number. For empty values kindly input 0";
            } else {
                // Ensures conversion of the value into an number
                output.value = Number(value)
                output.error = ''
            }
        }

        setstate((prev) => ({
            ...prev,
            input: modFacet(prev.input, oid, e.target.name, output.value),
            faults: modFacet(prev.faults, oid, e.target.name, output.error),
        }));
    };

    /**
     * Handles state updates for checkboxes and radio buttons in the `input` array.
     * - Checkboxes: Allows multiple selections, adding/removing values dynamically.
     * - Radio Buttons: Ensures only one value is selected per group.
     *
     * @param {Event} e - The input change event.
     * @param {string} oid - The unique section identifier.
     */
    const onOptionChange = (e: any, oid: string) => {
        const { posting } = state;
        if (posting) return;

        const { type, value, name } = e.target;

        setstate((prev) => ({
            ...prev,
            input: modFacet(prev.input, oid, name, value, type === "checkbox"),
            faults: modFacet(prev.faults, oid, name, ""), // Clear faults if any
        }));
    };

    /**
     * Handles the date selection from the DatePicker component.
     * Converts the selected date to a standardized format and updates the state.
     * Ensures the date is stored in UTC format and properly validated.
     *
     * @param {any} e - The event or date object received from the date picker.
     * @param {string} oid - The unique identifier for the section in `input` and `faults`.
     * @param {string} k - The key within the object where the date value should be stored.
     * @param {() => void} close - Function to close the date picker pop-up after selection.
     */
    const onDatePickerHandler = (e: any, oid: string, k: string, close?: () => void) => {
        const { posting } = state;
        if (posting) return;

        try {
            let inputValue: any;

            // ✅ Check if e is an event (user typed date)
            if (e?.target) {
                inputValue = e.target.value; // Extract typed value
            } else {
                inputValue = e; // Use selected date from pop-up
            }

            const dateString = new Date(inputValue);

            if (isNaN(dateString.getTime())) {
                setstate((prev) => ({
                    ...prev,
                    input: modFacet(prev.input, oid, k, null),
                    faults: modFacet(prev.faults, oid, k, "Kindly input a valid date"),
                }));
            }

            // Convert date to UTC format (ensuring consistency across time zones)
            const dateStringInUTC = new Date(
                Date.UTC(dateString.getFullYear(), dateString.getMonth(), dateString.getDate())
            );

            // Format the date for display (e.g., "dd/MM/yyyy")
            const displayFormat = formatDate(dateStringInUTC, "dd/MM/yyyy");

            // Update state with formatted date and clear any previous errors
            setstate((prev) => ({
                ...prev,
                input: modFacet(prev.input, oid, k, displayFormat),
                faults: modFacet(prev.faults, oid, k, ""),
            }));
        } catch (error) {
            setstate((prev) => ({
                ...prev,
                input: modFacet(prev.input, oid, k, null),
                faults: modFacet(prev.faults, oid, k, "Kindly input a valid date"),
            }));
        }

        // ✅ Only call close() if it's defined
        if (close) close();
    };

    /**
     * Selects the value of a specific field in the array based on the provided `oid` and `name`.
     * 
     * @param {Array} spfcArray - The array of to search through. Each contains multiple fields under an `oid` key.
     * @param {string} oid - The identifier for the section that holds the field you're interested in.
     * @param {string} xid - The name of the field whose value you want to retrieve.
     * 
     * @returns {any | undefined} - Returns the value of the field if it exists, or `undefined` if the field is not found.
     */
    const selectFacet = (spfcArray: any[], oid: string, xid: string): any | undefined => {
        for (const section of spfcArray) {
            if (section[oid]) {
                const field = section[oid].find((field: any) => Object.keys(field)[0] === xid);
                if (field) {
                    return field[xid]; // Return the value of the field
                }
            }
        }
        return undefined;
    };

    const sectionValidation = (oid: string) => {
        const { posting, input, faults, data, stride } = state

        if (!posting) {
            // Get the relevant sections for the given oid
            const faultsSection = faults.find(section => section[oid])?.[oid] || [];
            const inputSection = input.find(section => section[oid])?.[oid] || [];

            // 1. Check if any existing errors are present in faults
            const hasErrors = faultsSection.some(errorObj =>
                Object.values(errorObj)[0] !== ""
            );

            if (hasErrors) {
                console.log("Validation failed: Existing errors found.", faults);
                return false; // Stop validation if errors exist
            }

            let hasValidationError = false;
            let updatedFaults = [...faults]; // Copy faults state for modification

            // 2. Validate mandatory fields in input
            for (const { xid, properties, label } of data.mappedAXT) {
                const fieldProps = parseProperties(properties);
                const isMandatory = fieldProps.mandatory || false;

                const fieldData = inputSection.find((field: any) => field[xid] !== undefined);
                const value = fieldData ? fieldData[xid] : null;

                if (typeof value === "string") {
                    const event = {
                        target: {
                            name: xid,
                            required: isMandatory,
                            value: selectFacet(input, oid, xid),
                        }
                    }

                    let output = G_onInputBlurHandler(event, label);
                    output.error = output.error.replace(event.target.name, "")

                    if (output.error.length > 1) {
                        hasValidationError = true
                    }

                    updatedFaults = modFacet(updatedFaults, oid, xid, output.error);
                } else if (typeof value === "object") {
                    if (value.length < 1) {
                        hasValidationError = true;
                        const errorMessage = "Kindly select an item";

                        // Update faults with the error message
                        updatedFaults = modFacet(updatedFaults, oid, xid, errorMessage);
                    }
                }
            }

            if (hasValidationError) {
                setstate((prev: any) => ({
                    ...prev,
                    faults: updatedFaults,
                }));

                return
            }

            data.mappedAXT = [];
            const nextStride = stride + 1

            data.currentSection = data.sections[nextStride];

            /**
             * Extract the AXT data for the current section and map the short keys to full names.
             * This uses the `axtMapping` to convert the keys and returns a mapped version of the AXT.
             */
            const unmappedAXT = data.preview.find((item: any) => item.oid === data.currentSection.oid)?.axt || [];

            data.mappedAXT = unmappedAXT.map((axt: any) =>
                Object.keys(axt).reduce((mapped: any, key) => {
                    mapped[axtMapping[key]] = axt[key]; // Map short key to full key name
                    return mapped;
                }, {})
            );

            setstate((prev: any) => ({
                ...prev,
                data,
                stride: nextStride,
            }))
        }
    }



    const handleSelection = (event: any) => {
        let { posting } = state

        if (!posting) {
            setstate((prevProps: any) => ({
                ...prevProps,
                version: event.target.value
            }));
        }
    };

    const loadSelectedVersion = async () => {
        let { version } = state

        if (!version) {
            return
        }

        setstate((prevState: any) => ({
            ...prevState,
            posting: true,
        }))

        fetchAttributesForVersion(version)
    }

    const previewSelection = (e: any) => {
        e.preventDefault()


    }

    return (
        <React.Fragment>
            <FullScreenModal
                show={show}
                showOrHide={showOrHide}
                execAction={undefined}
                content={
                    <>
                        {
                            state.status === 'rejected' ? (
                                <>

                                </>
                            ) : state.status === 'fulfilled' ? (
                                <>
                                    {
                                        state.version ? (
                                            <>
                                                <div className="w-full px-2 h-full mx-auto flex md:flex-row flex-col gap-3 md:px-2.5 py-3 bg-gray-100 dark:bg-gray-900 md:overflow-hidden overflow-y-auto">
                                                    <div className="md:w-2/5 flex-none w-full px-2.5 md:px-2 pb-2 pt-2 sm:pb-2 flex flex-row-reverse">
                                                        <div className="lg:w-2/3 w-full flex md:flex-col flex-col-reverse gap-3">
                                                            <div className="w-full">
                                                                <div className="md:text-2xl text-xl mb-3 capitalize text-gray-900 dark:text-gray-100 flex gap-x-3 md:flex-col flex-row align-middle md:items-start items-center">
                                                                    {type.name}

                                                                    <div className="md:pt-0">
                                                                        {
                                                                            state.version === 'draft' ? (
                                                                                <span className="inline-flex rounded-md bg-yellow-400 dark:bg-yellow-600 dark:text-black px-2 py-[0.1875rem] text-xs text-black/80 gap-x-1">
                                                                                    {state.version.charAt(0).toUpperCase() + state.version.slice(1).toLowerCase()}
                                                                                    <span className="md:inline-flex hidden"> Version</span>
                                                                                </span>
                                                                            ) : (
                                                                                <div className="inline-flex rounded-md bg-emerald-600 dark:bg-emerald-700 dark:text-slate-100 px-2 py-[0.1875rem] text-xs text-white gap-x-1">
                                                                                    Published
                                                                                    <span className="md:inline-flex hidden">Version</span>
                                                                                </div>
                                                                            )
                                                                        }
                                                                    </div>
                                                                </div>

                                                                <p className="text-sm">
                                                                    Preview the form to verify its data capture
                                                                </p>
                                                            </div>

                                                            <ul className="w-full md:block flex justify-between align-middle md:pt-4 pt-6 pb-1">
                                                                {
                                                                    state.data.sections.map((section: any, index: any) => {
                                                                        return (
                                                                            <li key={section.oid} className={`flex md:flex-row flex-col ${index < (state.data.sections.length - 1) ? "w-full" : null} ${index < state.stride || index === state.stride ? "flex-grow" : "flex-shrink"
                                                                                } gap-x-3`}>
                                                                                <div className="flex md:flex-col flex-row items-center relative">
                                                                                    <div>
                                                                                        <div className="flex w-6 h-6 sm:w-6 sm:h-6 lg:w-8 lg:h-8 items-center justify-center">
                                                                                            {
                                                                                                index === state.stride ? (
                                                                                                    <svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                                                        <circle cx="12" cy="12" r="10" className="stroke-orange-500 dark:stroke-orange-600" strokeWidth="2" vectorEffect="non-scaling-stroke" fill="none" />
                                                                                                    </svg>
                                                                                                ) : index < state.stride ? (
                                                                                                    <svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                                                        <circle cx="12" cy="12" r="10" className="stroke-orange-500 dark:stroke-orange-600" strokeWidth="2" fill="none" vectorEffect="non-scaling-stroke" />
                                                                                                        <path d="M8 12l3 3l5-5" className="stroke-orange-500 dark:stroke-orange-600" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                                                                                    </svg>
                                                                                                ) : (
                                                                                                    <svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                                                        <circle cx="12" cy="12" r="10" className="stroke-slate-400 dark:stroke-slate-700" strokeWidth="2" fill="none" vectorEffect="non-scaling-stroke" />
                                                                                                    </svg>
                                                                                                )
                                                                                            }
                                                                                        </div>
                                                                                    </div>

                                                                                    {
                                                                                        (state.data.sections.length - 1) !== index ? (
                                                                                            <div className={`md:h-full md:w-px w-full h-px ${index < state.stride ? "bg-orange-500 dark:bg-orange-500" : "bg-slate-400 dark:bg-slate-700"} `}></div>
                                                                                        ) : null
                                                                                    }
                                                                                </div>

                                                                                <div className={`md:pb-5 md:block md:pt-1 pt-3 hidden ${index === state.stride ? "text-orange-600 w-full dark:text-orange-500" : "text-slate-600 dark:text-slate-300"}`}>
                                                                                    <p className="mb-3 md:text-base text-xs">
                                                                                        {section.label}
                                                                                    </p>
                                                                                </div>
                                                                            </li>
                                                                        )
                                                                    })
                                                                }
                                                            </ul>
                                                        </div>
                                                    </div>

                                                    <div className="md:flex-1 flex-grow bg-white dark:bg-gray-950 border dark:border-gray-700 border-gray-300 rounded-md w-full flex flex-col md:overflow-y-auto">
                                                        <div className="absolute top-0 right-0 w-full md:w-auto z-10">
                                                            <div className="md:px-2 px-2 md:py-2 py-1.5 md:bg-inherit dark:md:bg-inherit bg-gray-100 dark:bg-gray-900/80 md:backdrop-blur-none backdrop-blur-sm bg-opacity-65 w-full flex flex-row-reverse">
                                                                <button onClick={showOrHide} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer text-sm">
                                                                    <svg className="size-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="md:flex-1 w-full min-h-auto md:px-6 px-4 md:pt-4 pt-2">
                                                            <div className="w-full md:pt-0 pt-2">
                                                                <div className="w-full mb-2 md:pt-0 pt-1">
                                                                    <p className="md:text-xl text-lg text-slate-900 dark:text-white font-medium">
                                                                        {state.data.currentSection.label}
                                                                    </p>

                                                                    {
                                                                        state.data.currentSection.subtitle && (
                                                                            <p className="text-sm py-2 text-slate-600 dark:text-slate-400">
                                                                                {state.data.currentSection.subtitle}
                                                                            </p>
                                                                        )
                                                                    }

                                                                    <span className="text-red-500 dark:text-red-500 text-xs block">* Required</span>
                                                                </div>

                                                                <ul className="lg:w-3/5 w-full flex flex-col">
                                                                    {
                                                                        state.data.mappedAXT.map((axt: any) => {
                                                                            return (
                                                                                <React.Fragment key={axt.xid}>
                                                                                    <BlockBuster
                                                                                        axt={axt}
                                                                                        datum={state.data}
                                                                                        oid={state.data.currentSection.oid}
                                                                                        input={state.input}
                                                                                        faults={state.faults}
                                                                                        changeHandler={onChangeHandler}
                                                                                        blurHandler={onInputBlur}
                                                                                        optionHandler={onOptionChange}
                                                                                        dateHandler={onDatePickerHandler}
                                                                                    />
                                                                                </React.Fragment>
                                                                            )
                                                                        })
                                                                    }
                                                                </ul>
                                                            </div>
                                                        </div>

                                                        <div className="flex-none md:pb-2 pb-40 md:px-6 px-4 md:pt-4 pt-2">
                                                            <div className="lg:w-3/5 w-full">
                                                                <p className="text-sm flex gap-x-2 text-slate-500 pb-4">
                                                                    💡
                                                                    <span className="italic">
                                                                        Any data captured will not be saved.
                                                                    </span>
                                                                </p>

                                                                <div className="flex flex-row items-center align-middle pt-3 border-t dark:border-gray-700 border-gray-300 w-full justify-between pb-2">
                                                                    <div className="w-1/2">
                                                                        {
                                                                            state.stride > 0 ? (
                                                                                <button type="button" className="text-sm px-3 py-1 bg-inherit text-slate-600 dark:text-gray-400 hover:text-secondary dark:hover:text-gray-300 w-auto min-w-24 justify-center disabled:cursor-not-allowed rounded-md border border-transparent focus:outline-none">
                                                                                    <span className="flex flex-row items-center h-5 justify-center gap-x-3">
                                                                                        {
                                                                                            state.posting ? (
                                                                                                <i className="fad fa-spinner-third fa-xl fa-spin"></i>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <i className="fad fa-arrow-left fa-xl"></i>
                                                                                                    Previous
                                                                                                </>
                                                                                            )
                                                                                        }
                                                                                    </span>
                                                                                </button>
                                                                            ) : null
                                                                        }
                                                                    </div>


                                                                    <button type="button" onClick={() => sectionValidation(state.data.currentSection.oid)} className="w-auto min-w-24 justify-center disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-sm px-3 py-2 bg-orange-600 dark:bg-orange-500 text-white disabled:bg-orange-600 hover:bg-secondary focus:outline-none">
                                                                        <span className="flex flex-row items-center h-5 justify-center gap-x-3">
                                                                            {
                                                                                state.posting ? (
                                                                                    <i className="fad fa-spinner-third fa-xl fa-spin"></i>
                                                                                ) : (
                                                                                    <>
                                                                                        Next
                                                                                        <i className="fad fa-arrow-right fa-lg"></i>
                                                                                    </>
                                                                                )
                                                                            }
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="w-4 md:block hidden"></div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="space-y-3 w-full md:w-[470px] mx-auto h-full flex flex-col items-center justify-center align-middle">
                                                <div className="flex-none">
                                                    <h2 className="text-2xl mb-3 text-slate-700 dark:text-slate-200">
                                                        Choose a form to preview.
                                                    </h2>

                                                    <label className="block text-sm font-edium text-muted pb-6 dark:text-gray-400">
                                                        Two versions of this form exist, which would you like to preview?
                                                    </label>

                                                    <div className="flex gap-4 flex-col pb-4">
                                                        {
                                                            previewChoices.map((preview, index) => (
                                                                <div className="md:basis-1/2 w-full" key={`unpub-chng-${index}`}>
                                                                    <RadioButton
                                                                        value={preview.key}
                                                                        name={"example"}
                                                                        required={false}
                                                                        disabled={false}
                                                                        checked={state.version === preview.key}
                                                                        changeHandler={handleSelection}
                                                                        label={
                                                                            <div className="w-full flex md:flex-row items-center gap-3">
                                                                                <div className="w-20 h-auto flex items-center justify-center p-2">
                                                                                    <img src={preview.key === 'published' ? documentPublishedLight : documentDraftLight} className="h-auto" alt="" />
                                                                                </div>

                                                                                <div className="flex-1">
                                                                                    <p className="text-sm font-medium mb-1">
                                                                                        {preview.label}
                                                                                    </p>

                                                                                    <p className="text-sm font-normal tracking-tight text-gray-500 dark:text-gray-400">
                                                                                        {preview.subtitle}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        }
                                                                    />
                                                                </div>
                                                            ))
                                                        }
                                                    </div>

                                                    <div className="w-full flex items-center justify-center py-2">
                                                        <button type="button" onClick={loadSelectedVersion} className="w-auto min-w-24 justify-center disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-sm px-3 py-2 bg-orange-600 text-white disabled:bg-orange-600 hover:bg-secondary focus:outline-none" disabled={state.posting}>
                                                            <span className="flex flex-row items-center h-6 justify-center">
                                                                {
                                                                    state.posting ? (
                                                                        <i className="fad fa-spinner-third fa-xl fa-spin"></i>
                                                                    ) : (
                                                                        <span>
                                                                            Proceed
                                                                        </span>
                                                                    )
                                                                }
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }
                                </>
                            ) : (
                                <div className="py-2 px-6 w-full mx-auto h-full flex flex-col items-center justify-center align-middle">
                                    <Loading />
                                </div>
                            )
                        }
                    </>
                }
            />
        </React.Fragment >
    )
}